"use client"

import { createContext, useContext, useEffect, useMemo, useState } from "react"
import type { ReactNode } from "react"
import type { KeycloakTokenParsed } from "keycloak-js"
import { Grid } from "react-loader-spinner"

import { IdentitySetupGate } from "@/components/identity/identity-setup-gate"
import { UserService } from "@/lib/auth/keycloak"

type AuthContextValue = {
  authenticated: boolean
  initialized: boolean
  token?: string
  tokenParsed?: KeycloakTokenParsed
  /** OIDC-style display name (JWT `name`, then fallbacks). */
  displayName: string
  userEmail?: string
  userInitials: string
  username?: string
  login: () => Promise<void>
  logout: () => Promise<void>
  hasRealmRole: (roles: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialized, setInitialized] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  const [token, setToken] = useState<string | undefined>()
  const [tokenParsed, setTokenParsed] = useState<KeycloakTokenParsed | undefined>()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const syncAuthState = () => {
      if (!mounted) return
      setAuthenticated(UserService.isLoggedIn())
      setToken(UserService.getToken())
      setTokenParsed(UserService.getTokenParsed())
    }

    const unsubscribe = UserService.subscribeToTokenChanges(syncAuthState)

    UserService.requireLogin()
      .then(() => {
        if (!mounted) return
        syncAuthState()
        setInitialized(true)
      })
      .catch((err: unknown) => {
        if (!mounted) return
        setError(err instanceof Error ? err.message : String(err))
        setInitialized(true)
      })

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      authenticated,
      initialized,
      token,
      tokenParsed,
      displayName: UserService.getDisplayName(),
      userEmail: UserService.getEmail(),
      userInitials: UserService.getUserInitials(),
      username: UserService.getUsername(),
      login: UserService.login,
      logout: UserService.logout,
      hasRealmRole: UserService.hasRealmRole,
    }),
    [authenticated, initialized, token, tokenParsed]
  )

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <div className="max-w-xl rounded-lg border border-red-200 bg-red-50 p-6 text-red-900">
          <h1 className="mb-2 text-lg font-semibold">Authentication setup error</h1>
          <p className="text-sm">{error}</p>
        </div>
      </main>
    )
  }

  if (!initialized || !authenticated) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-8">
        <Grid
          visible={true}
          height="40"
          width="40"
          color="#7dd3fc"
          ariaLabel="grid-loading"
          radius="12.5"
          wrapperStyle={{}}
          wrapperClass="grid-wrapper"
        />
      </main>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      <IdentitySetupGate>{children}</IdentitySetupGate>
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider")
  }
  return value
}
